'use client';

import { useState } from 'react';
import { ChevronDown, ChevronRight, KeyRound, LogIn, LogOut } from 'lucide-react';
import { SheetPage, useResponsiveSheet } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { MockPersona } from '@/lib/mock';
import type { WidgetSettings } from '@/lib/settings';
import type { Person } from '@/lib/types';
import { signInAndReturn, signOut } from '@/lib/campus';
import { ProfileContext, useProfile } from '../components/ProfileContext';
import { CampusSvgProvider, CampusIcon } from '../components/CampusIcon';
import { Avatar } from '../components/Avatar';
import { HeaderGlow, SHEET_HEADER_GRADIENT } from '../components/SheetHeader';
import { PhotoAvatar } from '../components/PhotoAvatar';
import { PasswordForm } from '../components/AccountSection';
import { GivingMini } from '../components/GivingSection';
import { EmailChannel, EmailControls, TextControls } from '../components/LoopSection';
import { useProfileSession } from '../components/useProfileSession';
import {
  checkedAppearance,
  selectRow,
  useDrawerSections,
  type DrawerSection,
} from './drawerSections';
import {
  CARD,
  CTA_DANGER,
  CTA_TEAL,
  Kicker,
  PageBody,
  ProfileSheet,
  ToastProvider,
} from '../components/ui';

export interface LoginWidgetProps {
  apiBaseUrl: string;
  mock?: boolean;
  mockSignedOut?: boolean;
  mockPersona?: MockPersona;
  /** The full profile page the drawer points at; without it, MP's ProfileBaseURL, then the default */
  profileUrl?: string;
  /** Explicit overrides for the other links; anything not given comes from MP (CUSTOMWIDGETS), then the defaults */
  settings?: Partial<WidgetSettings>;
  blurPii?: boolean;
  /**
   * `trigger` (default): the header pill and its drawer. Inside the hamburger
   * menu the widget is split in two mounts: `menu-identity` (the person at
   * the top of the menu, linking to the full profile) and `menu-footer`
   * (Change password and Sign out under the menu).
   */
  variant?: 'trigger' | 'menu-identity' | 'menu-footer';
}

/**
 * The login widget: what the header shows instead of the stock
 * `<mpp-user-login>` dropdown. Signed out, a Sign in pill that runs MP's
 * own sign-in (the stock element stays on the page, hidden, and still
 * catches the OAuth callback every MP widget relies on). Signed in, the
 * person's face and first name; a tap opens a drawer from the right (a
 * bottom sheet on a phone) about the signed-in person: the way to the full
 * profile, giving said small (with the full page's Giving sheet and Online
 * Giving a tap away), their Email and Text switches with the lists they
 * get, and pinned under it all, Change password and Sign out. Details and
 * the household are edited on the full page.
 */
export function LoginWidget(props: LoginWidgetProps) {
  return (
    <ToastProvider>
      <LoginInner {...props} />
    </ToastProvider>
  );
}

function LoginInner({
  apiBaseUrl,
  mock = false,
  mockSignedOut = false,
  mockPersona,
  profileUrl: profileUrlProp,
  settings: overrides,
  blurPii = false,
  variant = 'trigger',
}: LoginWidgetProps) {
  const { status, ctx, svgMap, settings } = useProfileSession({
    apiBaseUrl,
    mock,
    mockSignedOut,
    mockPersona,
    settings: overrides,
  });
  const profileUrl = profileUrlProp ?? settings.profileUrl;
  const [open, setOpen] = useState(false);

  if (status === 'signedOut') {
    if (variant === 'menu-footer') return null;
    return (
      <button
        type="button"
        onClick={() => {
          if (mock) window.location.assign(window.location.pathname + '?mock=1&view=login');
          else signInAndReturn();
        }}
        className="inline-flex h-[var(--wbc-header-control,46px)] items-center gap-1.5 rounded-full bg-[#1c2b39] px-5 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
      >
        <LogIn className="h-4 w-4" strokeWidth={2.5} />
        Sign in
      </button>
    );
  }

  if (status === 'loading' || status === 'error' || !ctx) {
    if (variant === 'menu-footer') return null;
    return (
      <span
        className="inline-flex h-[var(--wbc-header-control,46px)] items-center gap-2 rounded-full bg-gray-100 pr-3 pl-1"
        aria-busy={status === 'loading'}
      >
        <span className="block h-10 w-10 animate-pulse rounded-full bg-gray-200" />
        <span className="hidden h-3 w-14 animate-pulse rounded bg-gray-200 sm:block" />
      </span>
    );
  }

  const me = ctx.data.me;
  const close = () => setOpen(false);

  if (variant === 'menu-identity') {
    return (
      <CampusSvgProvider value={svgMap}>
        <ProfileContext.Provider value={ctx}>
          <MenuIdentity person={me} profileUrl={profileUrl} blurPii={blurPii} />
        </ProfileContext.Provider>
      </CampusSvgProvider>
    );
  }

  if (variant === 'menu-footer') {
    return (
      <CampusSvgProvider value={svgMap}>
        <ProfileContext.Provider value={ctx}>
          <div className={blurPii ? 'blur-pii' : ''}>
            <MenuFooter onPassword={() => setOpen(true)} />
            <ProfileSheet open={open} onClose={close} maxWidth="max-w-md" title="Change password">
              <SheetPage name="main">
                <PasswordForm onDone={close} />
              </SheetPage>
            </ProfileSheet>
          </div>
        </ProfileContext.Provider>
      </CampusSvgProvider>
    );
  }

  return (
    <CampusSvgProvider value={svgMap}>
      <ProfileContext.Provider value={ctx}>
        <span className={blurPii ? 'blur-pii' : ''}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
            // Height comes from the site's --wbc-header-control variable (inherits through the shadow root), so Give, the hamburger and this pill share one number.
            className="group inline-flex h-[var(--wbc-header-control,46px)] items-center gap-2 rounded-full bg-gray-100 py-1 pr-3 pl-1 text-left transition-all hover:bg-gray-200 hover:shadow-sm active:scale-[0.98]"
          >
            <Avatar name={me.displayName} imageUrl={me.imageUrl} size={40} />
            <span className="pii hidden max-w-[10rem] truncate text-sm font-bold text-[#1c2b39] sm:block">
              {me.nickname || me.firstName}
            </span>
            <ChevronDown
              className="hidden h-4 w-4 text-gray-400 transition-colors group-hover:text-[#1c2b39] sm:block"
              strokeWidth={2.5}
            />
          </button>
          <ProfileSheet
            open={open}
            onClose={close}
            placement="right"
            maxWidth="max-w-md"
            header={<LoginHeader person={me} />}
            footer={<DrawerChores />}
          >
            <SheetPage name="main">
              <LoginMain profileUrl={profileUrl} onClose={close} />
            </SheetPage>
            <SheetPage name="password" title="Change password">
              <BackAfter>{(done) => <PasswordForm onDone={done} />}</BackAfter>
            </SheetPage>
            <SheetPage name="lists" title="Which emails">
              <PageBody>
                <EmailChannel person={me} />
              </PageBody>
            </SheetPage>
          </ProfileSheet>
        </span>
      </ProfileContext.Provider>
    </CampusSvgProvider>
  );
}

/** A form page whose "done" is Back to the drawer's main page, not closing the drawer. */
function BackAfter({ children }: { children: (done: () => void) => React.ReactNode }) {
  const { goBack } = useResponsiveSheet();
  return <>{children(goBack)}</>;
}

/**
 * The top of the hamburger menu: the signed-in person, the whole block a link
 * to the full profile. White on the menu's navy; the name in the display
 * face, "View full profile" under it. No campus here (Colton).
 */
function MenuIdentity({
  person,
  profileUrl,
  blurPii,
}: {
  person: Person;
  profileUrl: string;
  blurPii: boolean;
}) {
  return (
    <a
      href={profileUrl}
      className={`group flex items-center gap-4 py-2 text-white no-underline ${blurPii ? 'blur-pii' : ''}`}
    >
      <span className="rounded-full ring-2 ring-white/30">
        <Avatar name={person.displayName} imageUrl={person.imageUrl} size={56} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="pii font-display block truncate text-3xl leading-none tracking-wide text-white uppercase">
          {person.displayName}
        </span>
        <span className="mt-1.5 flex items-center gap-1 text-sm font-bold text-[#4cb8ab] group-hover:underline">
          View full profile
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      </span>
    </a>
  );
}

/** The bottom of the hamburger menu: the account chores, quiet, white on navy. */
function MenuFooter({ onPassword }: { onPassword: () => void }) {
  const { mock } = useProfile();
  const [signingOut, setSigningOut] = useState(false);
  const doSignOut = async () => {
    setSigningOut(true);
    if (mock) {
      window.location.assign(window.location.pathname + '?mock=1&view=login&signedout=1');
      return;
    }
    await signOut();
  };
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/15 py-4 text-white">
      <button
        type="button"
        onClick={onPassword}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 transition-colors hover:text-white"
      >
        <KeyRound className="h-4 w-4" strokeWidth={2.5} />
        Change password
      </button>
      <button
        type="button"
        onClick={doSignOut}
        disabled={signingOut}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#ff8a80] transition-colors hover:text-[#ffb4ab] disabled:opacity-60"
      >
        <LogOut className="h-4 w-4" strokeWidth={2.5} />
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

/**
 * The drawer's header: the signed-in person's face (tap to change the
 * photo), name and campus, in the navy gradient every sheet wears.
 */
function LoginHeader({ person }: { person: Person }) {
  const { data, update } = useProfile();
  const campus = data.household?.congregationName ?? null;
  const campusId = data.household?.congregationId ?? data.me.webCongregationId ?? null;
  const setImage = (imageUrl: string | null) =>
    update((d) => ({ ...d, me: { ...d.me, imageUrl } }));
  return (
    <div
      className="relative overflow-hidden px-6 pt-8 pb-6 text-white sm:px-7 sm:pt-9"
      style={{ background: SHEET_HEADER_GRADIENT }}
    >
      <HeaderGlow />
      <div className="relative z-10 flex items-center gap-4">
        <PhotoAvatar person={person} size={72} onChange={setImage} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-widest text-white/60 uppercase">You</p>
          <h2 className="pii font-display mt-0.5 truncate text-4xl leading-none tracking-wide uppercase">
            {person.displayName}
          </h2>
          {campus && (
            <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-white/75">
              <CampusIcon congregationId={campusId} className="h-4 w-4" />
              {campus}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** The drawer's main page: the way to the full profile, giving said small, the communication switches. The chores are pinned under it (DrawerChores). */
function LoginMain({ profileUrl, onClose }: { profileUrl: string; onClose: () => void }) {
  const { data } = useProfile();
  const { navigate } = useResponsiveSheet();
  const sections = useDrawerSections();
  const me = data.me;

  return (
    // Extra room at the end so the last card clears the pinned chores with air to spare when the page scrolls.
    <PageBody className="gap-6 pb-8">
      <a href={profileUrl} className={`${CTA_TEAL} w-full`} onClick={onClose}>
        View full profile
        <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
      </a>

      {sections.map((section) => (
        <PageSection key={section.id} section={section} onClose={onClose} />
      ))}

      <section className="flex flex-col gap-2">
        <Kicker>Giving</Kicker>
        <GivingMini profileUrl={profileUrl} onLeave={onClose} />
      </section>

      <section className="flex flex-col gap-2">
        <Kicker>Communication</Kicker>
        <div className={`${CARD} flex flex-col gap-2 px-4 py-2`}>
          <EmailControls person={me} onLists={() => navigate('lists')} />
          <div className="border-t border-gray-100" />
          <TextControls person={me} />
        </div>
      </section>
    </PageBody>
  );
}

/**
 * A page's own part of the drawer (drawerSections.ts): on the women's
 * conference page, her place and her picks. A tap closes the drawer and the
 * page opens whatever the row stands for.
 */
function PageSection({ section, onClose }: { section: DrawerSection; onClose: () => void }) {
  const look = checkedAppearance(section.appearance);
  const open = (id: string) => {
    onClose();
    selectRow(section.id, id);
  };

  // The drawer's own card, for a page that sends no appearance.
  if (!look) {
    return (
      <section className="flex flex-col gap-2">
        <Kicker>{section.kicker}</Kicker>
        <div className={`${CARD} flex flex-col divide-y divide-gray-100 px-2`}>
          {section.rows.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => open(row.id)}
              className="flex w-full items-center gap-3 rounded-xl px-2 py-3 text-left transition-transform hover:scale-[1.01] active:scale-[0.99]"
            >
              {row.imageUrl !== undefined || row.personName ? (
                <Avatar
                  name={row.personName ?? row.title}
                  imageUrl={row.imageUrl ?? null}
                  size={40}
                />
              ) : null}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-[#1c2b39]">{row.title}</span>
                {row.subtitle ? (
                  <span className="block truncate text-xs text-gray-500">{row.subtitle}</span>
                ) : null}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2.5} />
            </button>
          ))}
        </div>
      </section>
    );
  }

  // The page's own card: its ground, its type, its art. Values were checked
  // in checkedAppearance; they only ever reach style properties.
  return (
    <section className="flex flex-col gap-2">
      <Kicker>{section.kicker}</Kicker>
      <div
        className="relative overflow-hidden rounded-2xl shadow-md"
        style={{
          backgroundColor: look.background,
          backgroundImage: look.image ? `url("${look.image}")` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {look.scrim ? (
          <span
            aria-hidden
            className="absolute inset-0"
            style={{ backgroundColor: look.scrim, opacity: 0.45 }}
          />
        ) : null}
        {look.cornerArt ? (
          <img
            src={look.cornerArt}
            alt=""
            aria-hidden
            // A fixed size, not the card's height: with one row the card is
            // 70px tall and the art shrank to a single petal.
            className="pointer-events-none absolute -right-6 -bottom-14 h-36 w-auto opacity-90"
          />
        ) : null}
        <div className="relative flex flex-col px-2">
          {section.rows.map((row, i) => (
            <button
              key={row.id}
              type="button"
              onClick={() => open(row.id)}
              className="flex w-full items-center gap-3 rounded-xl px-2 py-3.5 text-left transition-transform hover:scale-[1.01] active:scale-[0.99]"
              style={i > 0 && look.divider ? { borderTop: `1px solid ${look.divider}` } : undefined}
            >
              {row.imageUrl !== undefined || row.personName ? (
                <span className="rounded-full" style={{ boxShadow: `0 0 0 2px ${look.ink}40` }}>
                  <Avatar
                    name={row.personName ?? row.title}
                    imageUrl={row.imageUrl ?? null}
                    size={44}
                  />
                </span>
              ) : null}
              <span className="min-w-0 flex-1 pr-14">
                <span
                  className="block truncate text-xl leading-tight"
                  style={{ color: look.ink, fontFamily: look.displayFont, fontWeight: 500 }}
                >
                  {row.title}
                </span>
                {row.subtitle ? (
                  <span className="mt-0.5 block truncate text-xs" style={{ color: look.muted }}>
                    {row.subtitle}
                  </span>
                ) : null}
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0"
                strokeWidth={2.5}
                style={{ color: look.ink }}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Pinned under the drawer's main page, outside the scroll area, so Change
 * password and Sign out are always in reach. The other pages (the password
 * form, the lists) keep their own bottom.
 */
function DrawerChores() {
  const { currentPage, navigate } = useResponsiveSheet();
  const { mock } = useProfile();
  const [signingOut, setSigningOut] = useState(false);
  if (currentPage !== 'main') return null;
  const doSignOut = async () => {
    setSigningOut(true);
    if (mock) {
      window.location.assign(window.location.pathname + '?mock=1&view=login&signedout=1');
      return;
    }
    await signOut();
  };
  return (
    <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
      <button
        type="button"
        onClick={() => navigate('password')}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 transition-colors hover:text-[#1c2b39]"
      >
        <KeyRound className="h-4 w-4 text-[#299c8f]" strokeWidth={2.5} />
        Change password
      </button>
      <button type="button" onClick={doSignOut} disabled={signingOut} className={CTA_DANGER}>
        <LogOut className="h-4 w-4" strokeWidth={2.5} />
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}
